import React, { useState, useLayoutEffect, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Text from '@/components/Text'

import { Row } from '../components'
import { ImageIcon } from '@/icon'
import Button from '@/components/Button'
import warning_icon from '@/asset/img/warning_icon.webp'
import { grayText2, primary } from '@/constant'
import { useMutation } from '@tanstack/react-query'
import { voucherCodePurchase } from '@/api'
import { confirm } from '@/components/Modal'
import { PayTypeEnum, PaymentStatusEnum } from '@/types'
import AssistantCode from '@/layout/AssistantCode'
import { Stack, Checkbox } from '@mui/material'
import { getDurationString } from '../../../Space/Order/utils'
import { Message } from '@cx/ui'

const Payment = ({
  orderCode,
  endTime,
  paymentUrl,
  paid,
  showTime,
  status,
  disableBack,
  voucherCode,
  payType,
  onVoucherCodePaid,
}: {
  disableBack?: boolean
  orderCode: string
  endTime: number
  paymentUrl: string
  amount: number
  paid: boolean
  showTime: boolean
  status?: number // 1:未支付；2:已支付；3:已退款；4:已取消 5:超时关闭
  voucherCode?: string
  payType?: number
  onVoucherCodePaid?: (message: string) => void
}) => {
  const navigate = useNavigate()
  const [seconds, setSecond] = useState<number>(endTime)

  const [agreement, setAgreement] = useState<boolean>(false)

  const voucherPurchaseMutation = useMutation(voucherCodePurchase, {
    onSuccess: () => {
      Message.success('支付成功')
      window.open(`${location.href}&paid_by=${PayTypeEnum.voucher}`, '_self')
    },
    onError: (err: string) => {
      // @ts-ignore
      onVoucherCodePaid(err ?? '支付失败')
    },
  })

  useEffect(() => {
    setSecond(endTime)
  }, [endTime])

  useLayoutEffect(() => {
    const countDown = setInterval(() => {
      if (endTime > 0) {
        setSecond((second) => {
          if (second <= 1) {
            navigate(0)
            return 0
          } else {
            return second - 1
          }
        })
      }
    }, 1000)

    return () => {
      clearInterval(countDown)
    }
  }, [endTime, navigate])

  const payPage = useCallback(() => {
    window.open(paymentUrl, '_self')
  }, [paymentUrl])

  const goBack = useCallback(() => {
    navigate('/console/organization/orders')
  }, [navigate])

  const onVoucherPay = useCallback(() => {
    confirm({
      title: '提示',
      content: '确定使用兑换码支付此订单？',
      closeAfterClose: true,
      // @ts-ignore
      onClose: null,
      onConfirm: () =>
        voucherPurchaseMutation.mutate({
          order_code: orderCode,
          // @ts-ignore
          redeem_code: voucherCode,
          // @ts-ignore
          pay_type: payType,
        }),
    })
  }, [orderCode, payType, voucherCode, voucherPurchaseMutation])

  const purchase = useCallback(() => {
    if (payType === PayTypeEnum.voucher && voucherCode) {
      onVoucherPay()
    } else if (payType === PayTypeEnum.alipay && paymentUrl) {
      payPage()
    }
  }, [onVoucherPay, payPage, payType, paymentUrl, voucherCode])

  const openPolicy = useCallback(() => {
    window.open('/service-agreement.html', '_blank')
  }, [])

  const checkAgreement = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreement(event?.target?.checked)
  }, [])

  return (
    <Stack sx={{ width: '100%' }}>
      {status === PaymentStatusEnum.unpaid && (
        <Row sx={{ justifyContent: 'flex-end' }}>
          <Checkbox checked={agreement} onChange={checkAgreement} />
          <Text
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: primary,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={openPolicy}
          >
            《长亭百川云平台产品服务协议》
          </Text>
        </Row>
      )}
      <Row sx={{ justifyContent: 'space-between', width: '100%' }}>
        <Row sx={{ alignItems: 'center' }}>
          {showTime && (
            <>
              <ImageIcon
                sx={{
                  width: '14px',
                  marginRight: '8px',
                  marginTop: '-1px',
                }}
                src={warning_icon}
              />
              <Text size='xs' sx={{ color: grayText2 }}>
                {seconds > 0
                  ? `剩余支付时间${getDurationString(seconds)}，否则订单将自动取消。`
                  : '订单已过期，请重新下单'}
              </Text>
            </>
          )}
        </Row>
        <Row sx={{ gap: '8px' }}>
          {seconds > 0 && status === PaymentStatusEnum.unpaid ? (
            <>
              <Button disabled={!agreement} type='highlight' onClick={purchase}>
                支付
              </Button>
            </>
          ) : (
            <>
              {paid && payType !== PayTypeEnum.voucher && (
                <AssistantCode>
                  <div>
                    <Button type='secondary'>申请发票</Button>
                  </div>
                </AssistantCode>
              )}
              {!disableBack && (
                <Button type='cancel' onClick={goBack}>
                  返回
                </Button>
              )}
            </>
          )}
        </Row>
      </Row>
    </Stack>
  )
}

export default Payment
